import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Download, Loader2 } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * QR code for the shop's quote request link, ready to print for the counter.
 *
 * Rendered at 1024px so it survives being put on a poster or a business card —
 * a screen-sized QR looks fine on screen and falls apart in print. The download
 * is a plain data-URL anchor, so nothing leaves the browser.
 */
const QR_PIXELS = 1024;

const QuoteFormQrDialog = ({ open, onOpenChange, url, shopName }) => {
  const [dataUrl, setDataUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open || !url) return;

    setFailed(false);
    setDataUrl('');

    QRCode.toDataURL(url, {
      width: QR_PIXELS,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#000000', light: '#ffffff' }
    })
      .then(setDataUrl)
      .catch(() => setFailed(true));
  }, [open, url]);

  const filename = `${(shopName || 'quote-request').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-qr.png`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Quote request QR code</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <div className="flex h-64 w-64 items-center justify-center rounded-lg bg-white p-3">
            {dataUrl ? (
              <img src={dataUrl} alt={`QR code for ${url}`} className="h-full w-full" />
            ) : failed ? (
              <p className="px-4 text-center text-sm text-muted-foreground">
                Could not generate the code
              </p>
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            )}
          </div>

          <p className="break-all text-center font-mono text-xs text-muted-foreground">{url}</p>

          <Button asChild disabled={!dataUrl} className="w-full">
            <a
              href={dataUrl || undefined}
              download={filename}
              onClick={(event) => {
                if (!dataUrl) {
                  event.preventDefault();
                  return;
                }
                toast.success('QR code downloaded');
              }}
            >
              <Download className="h-4 w-4" />
              Download PNG
            </a>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteFormQrDialog;
